import {
  ChevronLeft,
  ChevronRight,
  Download,
  Maximize2,
  Minus,
  Plus,
  RotateCcw,
  RotateCw,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { DmsAttachment } from '@erp/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { downloadDmsAttachment, useDmsPreview } from '@/features/dms/api/use-dms';
import { formatDateTime } from '@/lib/formatters';

const TEXT_PREVIEW_MAX_CHARACTERS = 500_000;

function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (blob === undefined) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function isTextAttachment(attachment: DmsAttachment): boolean {
  return (
    attachment.mimeType.startsWith('text/') ||
    /\.(txt|csv|json|log)$/iu.test(attachment.originalName)
  );
}

export interface DocumentPreviewDialogProps {
  attachment: DmsAttachment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentPreviewDialog({
  attachment,
  open,
  onOpenChange,
}: DocumentPreviewDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const preview = useDmsPreview(attachment, open);
  const objectUrl = useObjectUrl(preview.data);
  const viewerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [page, setPage] = useState(1);
  const [textContent, setTextContent] = useState('');
  const [textTruncated, setTextTruncated] = useState(false);

  useEffect(() => {
    setZoom(100);
    setRotation(0);
    setPage(1);
  }, [attachment?.id]);

  useEffect(() => {
    if (preview.data === undefined || attachment === null || !isTextAttachment(attachment)) {
      setTextContent('');
      setTextTruncated(false);
      return;
    }
    let active = true;
    void preview.data.text().then((value) => {
      if (active) {
        setTextTruncated(value.length > TEXT_PREVIEW_MAX_CHARACTERS);
        setTextContent(value.slice(0, TEXT_PREVIEW_MAX_CHARACTERS));
      }
    });
    return () => {
      active = false;
    };
  }, [attachment, preview.data]);

  const pdfUrl = useMemo(() => {
    if (objectUrl === null) {
      return null;
    }
    return `${objectUrl}#page=${String(page)}&zoom=${String(zoom)}`;
  }, [objectUrl, page, zoom]);

  async function download(): Promise<void> {
    if (attachment === null) {
      return;
    }
    try {
      await downloadDmsAttachment(attachment);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('dms.errors.download'));
    }
  }

  async function toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement === null) {
      await viewerRef.current?.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  }

  function renderViewer(): JSX.Element {
    if (preview.isPending) {
      return <Skeleton className="h-[65dvh] w-full" />;
    }
    if (preview.isError || objectUrl === null || attachment === null) {
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-3" role="alert">
          <p className="text-sm text-destructive">{t('dms.preview.error')}</p>
          <Button variant="outline" onClick={() => void preview.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      );
    }

    if (attachment.mimeType === 'application/pdf') {
      return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-center gap-1">
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.previousPage')}
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <label className="flex items-center gap-2 text-sm">
              {t('dms.preview.page')}
              <input
                className="h-9 w-16 rounded-md border border-input bg-background px-2 text-center"
                type="number"
                min={1}
                value={page}
                onChange={(event) => setPage(Math.max(1, Number(event.target.value) || 1))}
              />
            </label>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.nextPage')}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.zoomOut')}
              disabled={zoom <= 50}
              onClick={() => setZoom((current) => Math.max(50, current - 25))}
            >
              <Minus className="h-4 w-4" aria-hidden="true" />
            </Button>
            <span className="w-14 text-center text-sm tabular-nums">{zoom}%</span>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.zoomIn')}
              disabled={zoom >= 300}
              onClick={() => setZoom((current) => Math.min(300, current + 25))}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.fullscreen')}
              onClick={() => void toggleFullscreen()}
            >
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <iframe
            key={pdfUrl}
            title={attachment.originalName}
            src={pdfUrl ?? undefined}
            className="min-h-[60dvh] w-full flex-1 rounded-md border border-border bg-muted"
          />
        </div>
      );
    }

    if (attachment.mimeType.startsWith('image/')) {
      return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-center gap-1">
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.rotateLeft')}
              onClick={() => setRotation((current) => current - 90)}
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
            </Button>
            <label className="flex items-center gap-2 text-sm">
              {t('dms.preview.zoom')}
              <input
                type="range"
                min={25}
                max={300}
                step={5}
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
              />
              <span className="w-12 tabular-nums">{zoom}%</span>
            </label>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.rotateRight')}
              onClick={() => setRotation((current) => current + 90)}
            >
              <RotateCw className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('dms.preview.fullscreen')}
              onClick={() => void toggleFullscreen()}
            >
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <div className="flex min-h-[60dvh] flex-1 items-center justify-center overflow-auto rounded-md border border-border bg-muted/40 p-4">
            <img
              src={objectUrl}
              alt={attachment.title ?? attachment.originalName}
              className="max-w-none transition-transform"
              style={{
                width: `${String(zoom)}%`,
                transform: `rotate(${String(rotation)}deg)`,
              }}
            />
          </div>
        </div>
      );
    }

    if (isTextAttachment(attachment)) {
      return (
        <div className="max-h-[65dvh] overflow-auto rounded-md border border-border bg-muted/30">
          {textTruncated ? (
            <p className="sticky top-0 border-b border-border bg-background p-2 text-xs text-muted-foreground">
              {t('dms.preview.textTruncated')}
            </p>
          ) : null}
          <ol className="min-w-max py-3 font-mono text-xs leading-5">
            {textContent.split(/\r?\n/u).map((line, index) => (
              <li key={`${String(index)}-${line.slice(0, 12)}`} className="flex px-3">
                <span className="mr-4 w-10 select-none text-right text-muted-foreground">
                  {index + 1}
                </span>
                <span className="whitespace-pre">{line || ' '}</span>
              </li>
            ))}
          </ol>
        </div>
      );
    }

    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-4 rounded-md border border-border p-8 text-center">
        <div>
          <p className="font-medium">{attachment.originalName}</p>
          <p className="mt-1 text-sm text-muted-foreground">{attachment.mimeType}</p>
          <p className="text-sm text-muted-foreground">
            {t('dms.preview.uploadedAt', {
              date: formatDateTime(attachment.createdAt, i18n.language),
            })}
          </p>
        </div>
        <Button onClick={() => void download()}>
          <Download className="h-4 w-4" aria-hidden="true" />
          {t('dms.actions.download')}
        </Button>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} className="max-w-6xl overflow-hidden">
        <DialogHeader className="pr-10">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <DialogTitle>{attachment?.title ?? attachment?.originalName ?? ''}</DialogTitle>
              <DialogDescription>{attachment?.originalName ?? ''}</DialogDescription>
            </div>
            {attachment === null ? null : (
              <Button variant="outline" size="sm" onClick={() => void download()}>
                <Download className="h-4 w-4" aria-hidden="true" />
                {t('dms.actions.download')}
              </Button>
            )}
          </div>
        </DialogHeader>
        <div
          ref={viewerRef}
          className="flex min-h-0 flex-col bg-background p-1 fullscreen:h-screen fullscreen:p-4"
        >
          {renderViewer()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
